import { useState, useEffect } from 'react'
import { Spinner } from '@heroui/react'
import { BarChart3, TrendingUp, Layers, ShoppingBag } from 'lucide-react'
import { funnelApi } from '../../api'
import type { AnalyticsSummary } from '../../types'
import { MetricCard } from './MetricCard'
import { FunnelPerformanceChart } from './FunnelPerformanceChart'
import { ActivityTimeline } from './ActivityTimeline'
import { ConversionFunnel } from './ConversionFunnel'

export function AnalyticsDashboard() {
  const [data, setData] = useState<AnalyticsSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadAnalytics()
  }, [])

  async function loadAnalytics() {
    try {
      setLoading(true)
      setError(null)
      const response = await funnelApi.getAnalytics()
      setData(response.data)
    } catch (err) {
      console.error('Failed to load analytics:', err)
      setError('Failed to load analytics data')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" color="primary" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="text-tf-critical mb-2">
          <svg
            className="w-12 h-12"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <p className="text-tf-text-muted font-medium">{error || 'Failed to load analytics'}</p>
        <button
          onClick={loadAnalytics}
          className="mt-4 px-4 py-2 bg-fn-primary text-fn-primary-text rounded-lg hover:bg-fn-primary-hover transition"
        >
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Funnels"
          value={data.totalFunnels}
          icon={<BarChart3 className="w-6 h-6" />}
          color="var(--fn-primary)"
        />
        <MetricCard
          title="Active Funnels"
          value={data.activeFunnels}
          icon={<TrendingUp className="w-6 h-6" />}
          color="#8b5cf6"
        />
        <MetricCard
          title="Total Steps"
          value={data.totalSteps}
          icon={<Layers className="w-6 h-6" />}
          color="#8b5cf6"
        />
        <MetricCard
          title="Total Offers"
          value={data.totalOffers}
          icon={<ShoppingBag className="w-6 h-6" />}
          color="#10b981"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <FunnelPerformanceChart data={data} />
        <ConversionFunnel data={data} />
      </div>

      {/* Activity Timeline */}
      <ActivityTimeline data={data} />
    </div>
  )
}
